import { Flex, InlineBanner, Spinner, Box } from '@cad-arc';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { EsriMapViewTooltip } from '../../../esri/components/esri-map-view-tooltip';
import { getMapKey } from '../../../esri/helpers/utils';
import { useEsriEmbeddedBaseMap } from '../../../esri/base-maps/use-esri-embedded-base-map';
import { useEsriMapMarkers } from '../../../esri/hooks/use-esri-map-markers';
import { ZoomInAndOutControl } from '../../../esri/components/zoom-in-and-out-control';
import { CustomMapMarker } from '../../../esri/types';
import { LocationStoreView } from '../../types/locations';
import { LocationDetails } from './location-details';
import { LocationMapContainer } from './location-map-container';
import { LocationDisplayProps } from './types';
import { MapLocationMetadata } from './map-location-metadata';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { errorOccurredWhile } from '../../../common/helpers/error-occurred-while';

export const EsriLocationMap = observer(
    ({
        store,
        isLoadingResults,
        query,
        onClickRow,
        moveFocusNext,
        inputRef,
        showLocationDetails,
        isCliMode,
        size,
    }: LocationDisplayProps) => {
        const { list } = store.data;
        // Property access for mobx update. (required, Otherwise mobx doesn't update and state doesn't change)
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        list.length;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        store.highlightedLocationIndex;

        const [selectedLocation, setSelectedLocation] = useState<LocationStoreView | null>(null);
        const [showUnverifiedAddressIndicator, setShowUnverifiedAddressIndicator] = useState(false);

        const { subpremiseSupport } = useEnabledFeatures();

        const { mapView, mapComponent, isMapLoadingFailed } = useEsriEmbeddedBaseMap({
            center: {
                latitude: store.data.list[0]?.latitude ?? 0,
                longitude: store.data.list[0]?.longitude ?? 0,
            },
        });

        const esriMarkerControls = useEsriMapMarkers([], mapView, subpremiseSupport);

        function getMapMarkers(data: LocationStoreView[]): CustomMapMarker[] {
            return data.map((ar, idx) => {
                const mapKey = getMapKey(idx).toUpperCase();
                return {
                    id: `${ar.id}`,
                    location: { lat: ar.latitude ?? 0, lng: ar.longitude ?? 0 },
                    label: mapKey,
                    letterIndex: mapKey,
                    selected: false,
                    tip: ar.streetAddress ?? '',
                };
            });
        }

        /**
         * We do this reactively so that the map can update fully, then pan.
         * When marker updates are in progress, mapViewController becomes undefined.
         */
        const mapViewController = mapView;
        useEffect(
            function panToSelectedLocation() {
                if (selectedLocation !== null && mapViewController !== undefined) {
                    mapViewController
                        .goTo(
                            [selectedLocation.longitude, selectedLocation.latitude],
                            {
                                animate: false,
                            }
                            // we no-op catch this error because ESRI will throw when one goTo interrupts another,
                            // but this will be very noisy and is not something worth logging on our end
                        )
                        .catch(() => {});
                }
            },
            [selectedLocation, mapViewController]
        );

        const onLocationSelected = useCallback(
            function onLocationSelected(
                location: LocationStoreView,
                index: number,
                userInputSource?: { source: 'keyboard' | 'mouse' }
            ): void {
                store.setHighlightedLocationIndex(index, userInputSource);
                if (location.isForcedResult) {
                    setShowUnverifiedAddressIndicator(true);
                } else {
                    setShowUnverifiedAddressIndicator(false);
                    esriMarkerControls.updateSelectedMarker(`${location.id}`);
                    setSelectedLocation(location);
                }
            },
            [esriMarkerControls, store]
        );

        useEffect(
            function updateMapMarkersAndSelectedLocation() {
                const disposer = autorun(() => {
                    esriMarkerControls.updateMarkers(getMapMarkers(store.data.list));
                    if (store.highlightedLocationIndex !== null) {
                        const selected = store.data.list[store.highlightedLocationIndex];
                        if (selected !== undefined) {
                            onLocationSelected(selected, store.highlightedLocationIndex);
                        }
                    }
                });
                return () => disposer();
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            []
        );

        return (
            <LocationMapContainer
                isLoadingResults={isLoadingResults}
                onLocationSelected={onLocationSelected}
                query={query}
                showUnverifiedAddressIndicator={showUnverifiedAddressIndicator}
                store={store}
                inputRef={inputRef}
                moveFocusNext={moveFocusNext}
                onClickRow={onClickRow}
                isCliMode={isCliMode}
                size={size}
            >
                {esriMarkerControls.currentTooltip !== null && (
                    <EsriMapViewTooltip {...esriMarkerControls.currentTooltip} />
                )}

                {!mapView?.initialized && (
                    <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                        <Spinner />
                    </Flex>
                )}
                {!showUnverifiedAddressIndicator && <ZoomInAndOutControl mapView={mapView} />}
                <Flex flexDirection="column" h="full" w="full">
                    {isMapLoadingFailed ? (
                        <InlineBanner
                            status="error"
                            description={errorOccurredWhile('loading the map')}
                        />
                    ) : (
                        <Box flex={1}>
                            {mapComponent}
                        </Box>
                    )}
                    {showLocationDetails && !subpremiseSupport ? (
                        <LocationDetails selectedLocation={selectedLocation} size={size} />
                    ) : null}

                    {subpremiseSupport && (
                        <MapLocationMetadata selectedLocation={selectedLocation} size={size} />
                    )}
                </Flex>
            </LocationMapContainer>
        );
    }
);
